import { useMutation, useQueryClient, type QueryClient } from '@tanstack/react-query';
import {
  schedulingApi,
  type AppointmentResponse,
  type BookStaffAppointmentRequest,
  type CorrectableStatus,
  type CreateWalkInRequest,
  type QueueVitalsBody,
  type QuickRegisterPatientRequest,
  type PatientPickOption,
  type QueueCopilotBoardResponse,
} from '../infrastructure/scheduling.api';
import { schedulingKeys } from './scheduling.query';

/**
 * Commands — scheduling write use cases.
 *
 * Queue actions are **optimistic**: `onMutate` patches the cached queue board so the card moves lane
 * (or updates) instantly, then `onSettled` reconciles with the server in the background. This removes
 * the old "board freezes for a few seconds after every action" delay, where the UI only updated once a
 * full refetch round-tripped. `onError` restores the pre-action snapshot so a rejected action never
 * leaves a stale optimistic card behind.
 *
 * Patches target the queue key by **prefix** (`schedulingKeys.queue()` with no clinic id) via
 * `setQueriesData`, so the command layer needs no clinic id and only ever touches the active clinic's
 * cached board.
 */

function useSchedulingInvalidation() {
  const queryClient = useQueryClient();
  return () => queryClient.invalidateQueries({ queryKey: schedulingKeys.all });
}

/** How a mutation optimistically changes the board. `null` from the resolver means "no optimistic change". */
type QueueEffect =
  | { kind: 'patch'; id: string; patch: Partial<AppointmentResponse> }
  | { kind: 'remove'; id: string }
  | { kind: 'swap'; firstId: string; secondId: string; firstOrder: number; secondOrder: number };

interface QueueMutationContext {
  snapshots?: [readonly unknown[], unknown][];
}

interface AppointmentCreationCommand<TRequest> {
  request: TRequest;
  /** Client-known display data used to render the completed write without waiting for a read-model refetch. */
  patientName?: string;
}

function upsertAppointment(items: AppointmentResponse[], appointment: AppointmentResponse): AppointmentResponse[] {
  const exists = items.some((item) => item.id === appointment.id);
  return exists
    ? items.map((item) => item.id === appointment.id ? appointment : item)
    : [...items, appointment];
}

function withPatientName(appointment: AppointmentResponse, patientName?: string): AppointmentResponse {
  return appointment.patientName || !patientName ? appointment : { ...appointment, patientName };
}

function hydrateWalkInCaches(queryClient: QueryClient, appointment: AppointmentResponse) {
  queryClient.setQueriesData<AppointmentResponse[]>({ queryKey: schedulingKeys.queue() }, (old) =>
    old ? upsertAppointment(old, appointment) : old,
  );
  queryClient.setQueriesData<QueueCopilotBoardResponse>(
    { queryKey: schedulingKeys.copilotBoard() },
    (old) => old ? { ...old, rows: upsertAppointment(old.rows, appointment) } : old,
  );
}

function hydrateAppointmentCaches(queryClient: QueryClient, appointment: AppointmentResponse) {
  const start = Date.parse(appointment.startTime);
  queryClient
    .getQueriesData<AppointmentResponse[]>({ queryKey: ['scheduling', 'appointments'] })
    .forEach(([key, items]) => {
      if (!items) return;
      const params = key.at(-1);
      if (!params || typeof params !== 'object' || !('from' in params) || !('to' in params)) return;
      const window = params as { from: string; to: string; providerId?: string };
      const belongsToWindow =
        start >= Date.parse(window.from)
        && start < Date.parse(window.to)
        && (!window.providerId || window.providerId === appointment.providerId);
      if (belongsToWindow) queryClient.setQueryData(key, upsertAppointment(items, appointment));
    });
}

function applyEffect(items: AppointmentResponse[], effect: QueueEffect): AppointmentResponse[] {
  if (effect.kind === 'remove') {
    return items.filter((item) => item.id !== effect.id);
  }
  if (effect.kind === 'swap') {
    return items.map(item => {
      if (item.id === effect.firstId) return { ...item, queueOrder: effect.secondOrder };
      if (item.id === effect.secondId) return { ...item, queueOrder: effect.firstOrder };
      return item;
    });
  }
  return items.map((item) => (item.id === effect.id ? { ...item, ...effect.patch } : item));
}

function patchQueueCaches(queryClient: QueryClient, effect: QueueEffect) {
  queryClient.setQueriesData<AppointmentResponse[]>({ queryKey: schedulingKeys.queue() }, (old) =>
    old ? applyEffect(old, effect) : old,
  );
  queryClient.setQueriesData<QueueCopilotBoardResponse>(
    { queryKey: schedulingKeys.copilotBoard() },
    old => old ? { ...old, rows: applyEffect(old.rows, effect) } : old,
  );
}

/**
 * Builds an optimistic queue mutation: applies `optimistic(vars)` to the cached board before the request,
 * rolls back on error, and invalidates the queue + copilot board on settle.
 */
function useQueueMutation<TVars, TData>(
  mutationFn: (vars: TVars) => Promise<TData>,
  optimistic?: (vars: TVars) => QueueEffect | null,
  hydrateSuccess?: (data: TData, vars: TVars, queryClient: QueryClient) => void,
) {
  const queryClient = useQueryClient();
  return useMutation<TData, unknown, TVars, QueueMutationContext>({
    mutationFn,
    onMutate: async (vars) => {
      const effect = optimistic?.(vars);
      if (!effect) return {};
      // Stop any in-flight board refetch from clobbering the optimistic patch mid-flight.
      await Promise.all([
        queryClient.cancelQueries({ queryKey: schedulingKeys.queue() }),
        queryClient.cancelQueries({ queryKey: schedulingKeys.copilotBoard() }),
      ]);
      const snapshots: [readonly unknown[], unknown][] = [
        ...queryClient.getQueriesData<AppointmentResponse[]>({ queryKey: schedulingKeys.queue() }),
        ...queryClient.getQueriesData<QueueCopilotBoardResponse>({ queryKey: schedulingKeys.copilotBoard() }),
      ];
      patchQueueCaches(queryClient, effect);
      return { snapshots };
    },
    onError: (_error, _vars, context) => {
      context?.snapshots?.forEach(([key, data]) => queryClient.setQueryData(key, data));
    },
    onSuccess: (data, vars) => {
      hydrateSuccess?.(data, vars, queryClient);
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: schedulingKeys.queue() });
      void queryClient.invalidateQueries({ queryKey: schedulingKeys.copilotBoard() });
      // Completing/cancelling a visit changes the Completed lane total.
      void queryClient.invalidateQueries({ queryKey: schedulingKeys.completedCount() });
    },
  });
}

export function useBookAppointmentCommand() {
  const queryClient = useQueryClient();
  const invalidate = useSchedulingInvalidation();
  return useMutation({
    mutationFn: ({ request }: AppointmentCreationCommand<BookStaffAppointmentRequest>) =>
      schedulingApi.book(request),
    onSuccess: (created, variables) => {
      hydrateAppointmentCaches(queryClient, withPatientName(created, variables.patientName));
      void invalidate();
    },
  });
}

/**
 * Adds a walk-in. The walk-in is created already checked in (with a queue token) so it lands directly on
 * the board's Checked-in lane. The new token is server-assigned, so the completed server response is inserted
 * into active queue caches before an authoritative background refetch reconciles the board.
 */
export function useCreateWalkInCommand() {
  const queryClient = useQueryClient();
  const invalidate = useSchedulingInvalidation();
  return useMutation({
    mutationFn: ({ request }: AppointmentCreationCommand<CreateWalkInRequest>) =>
      schedulingApi.createWalkIn(request),
    onSuccess: (created, variables) => {
      hydrateWalkInCaches(queryClient, withPatientName(created, variables.patientName));
      void invalidate();
    },
  });
}

export function useConfirmAppointmentCommand() {
  const invalidate = useSchedulingInvalidation();
  return useMutation({
    mutationFn: (appointmentId: string) => schedulingApi.confirm(appointmentId),
    onSuccess: () => void invalidate(),
  });
}

export function useRejectAppointmentCommand() {
  const invalidate = useSchedulingInvalidation();
  return useMutation({
    mutationFn: ({ appointmentId, reason }: { appointmentId: string; reason?: string }) =>
      schedulingApi.reject(appointmentId, reason),
    onSuccess: () => void invalidate(),
  });
}

export function useCheckInCommand() {
  return useQueueMutation(
    ({ appointmentId, patientId }: { appointmentId: string; patientId?: string }) =>
      schedulingApi.checkIn(appointmentId, patientId),
    ({ appointmentId, patientId }) => ({
      kind: 'patch',
      id: appointmentId,
      patch: { status: 'CheckedIn', isSkipped: false, ...(patientId ? { patientId } : {}) },
    }),
  );
}

export function useStartAppointmentCommand() {
  return useQueueMutation(
    (appointmentId: string) => schedulingApi.start(appointmentId),
    (appointmentId) => ({ kind: 'patch', id: appointmentId, patch: { status: 'InProgress' } }),
    (started, _appointmentId, queryClient) => patchQueueCaches(queryClient, {
      kind: 'patch',
      id: started.id,
      patch: {
        status: started.status,
        startedAt: started.startedAt,
        visitId: started.visitId,
        updatedAt: started.updatedAt,
      },
    }),
  );
}

// ---- Smart Queue copilot ----

export function useRefreshRecommendationsCommand() {
  const invalidate = useSchedulingInvalidation();
  return useMutation({
    mutationFn: () => schedulingApi.refreshRecommendations(),
    onSuccess: () => void invalidate(),
  });
}

export function useApproveRecommendationCommand() {
  const invalidate = useSchedulingInvalidation();
  return useMutation({
    mutationFn: (recommendationId: string) => schedulingApi.approveRecommendation(recommendationId),
    onSuccess: () => void invalidate(),
  });
}

export function useRejectRecommendationCommand() {
  const invalidate = useSchedulingInvalidation();
  return useMutation({
    mutationFn: (recommendationId: string) => schedulingApi.rejectRecommendation(recommendationId),
    onSuccess: () => void invalidate(),
  });
}

/** Toggles clinic-wide Smart Queue assistance (SmartQueueEnabled). Refreshes the copilot board + queue. */
export function useSetSmartQueueAssistanceCommand() {
  const invalidate = useSchedulingInvalidation();
  return useMutation({
    mutationFn: (enabled: boolean) => schedulingApi.setSmartQueueAssistance(enabled),
    onSuccess: () => void invalidate(),
  });
}

/**
 * Ensures a checked-in appointment has a linked draft visit so quick intake can be recorded before the doctor
 * starts. Invalidates the scheduling caches so the board picks up the new visit link (drives the "Intake ✓" state).
 */
export function useEnsureQueueDraftVisitCommand() {
  const invalidate = useSchedulingInvalidation();
  return useMutation({
    mutationFn: (appointmentId: string) => schedulingApi.ensureDraftVisit(appointmentId),
    onSuccess: () => void invalidate(),
  });
}

export function useCompleteAppointmentCommand() {
  return useQueueMutation(
    (appointmentId: string) => schedulingApi.complete(appointmentId),
    (appointmentId) => ({ kind: 'patch', id: appointmentId, patch: { status: 'Completed' } }),
  );
}

export function useCancelAppointmentCommand() {
  return useQueueMutation(
    ({ appointmentId, reason }: { appointmentId: string; reason?: string }) =>
      schedulingApi.cancel(appointmentId, reason),
    ({ appointmentId }) => ({ kind: 'remove', id: appointmentId }),
  );
}

export function useMarkNoShowCommand() {
  return useQueueMutation(
    (appointmentId: string) => schedulingApi.markNoShow(appointmentId),
    (appointmentId) => ({ kind: 'remove', id: appointmentId }),
  );
}

export function useSkipQueueItemCommand() {
  return useQueueMutation(
    ({ appointmentId, reason }: { appointmentId: string; reason?: string }) =>
      schedulingApi.skip(appointmentId, reason),
    ({ appointmentId, reason }) => ({
      kind: 'patch',
      id: appointmentId,
      patch: { isSkipped: true, ...(reason ? { skipReason: reason } : {}) },
    }),
  );
}

export function useUnskipQueueItemCommand() {
  return useQueueMutation(
    (appointmentId: string) => schedulingApi.unskip(appointmentId),
    (appointmentId) => ({ kind: 'patch', id: appointmentId, patch: { isSkipped: false, skipReason: null } }),
  );
}

export function useAssignQueueProviderCommand() {
  return useQueueMutation(
    ({ appointmentId, providerId }: { appointmentId: string; providerId: string }) =>
      schedulingApi.assignProvider(appointmentId, providerId),
    ({ appointmentId, providerId }) => ({ kind: 'patch', id: appointmentId, patch: { providerId } }),
  );
}

/** On-demand authoritative read used as the acknowledgement step before a staff reschedule. */
export function useBookingAdvisoriesCommand() {
  return useMutation({
    mutationFn: ({ appointmentId, proposedStartTime }: { appointmentId: string; proposedStartTime: string }) =>
      schedulingApi.getBookingAdvisories(appointmentId, proposedStartTime),
  });
}

/** Moves an appointment, then invalidates every scheduling surface so all hosts refetch authoritative state. */
export function useRescheduleAppointmentCommand() {
  const invalidate = useSchedulingInvalidation();
  return useMutation({
    mutationFn: ({
      appointmentId,
      newStartTime,
      reason,
    }: {
      appointmentId: string;
      newStartTime: string;
      reason?: string;
    }) => schedulingApi.reschedule(appointmentId, newStartTime, reason),
    onSuccess: () => void invalidate(),
  });
}

export function useLinkAppointmentPatientCommand() {
  return useQueueMutation(
    ({ appointmentId, patient }: { appointmentId: string; patient: PatientPickOption }) =>
      schedulingApi.linkPatient(appointmentId, patient.id),
    ({ appointmentId, patient }) => ({
      kind: 'patch',
      id: appointmentId,
      patch: {
        patientId: patient.id,
        patientName: patient.fullName,
        tempPatientName: null,
        tempPatientPhone: null,
      },
    }),
  );
}

export function useTransferInProgressProviderCommand() {
  return useQueueMutation(
    ({ appointmentId, receivingProviderId, reason }: { appointmentId: string; receivingProviderId: string; reason: string }) =>
      schedulingApi.transferInProgressProvider(appointmentId, receivingProviderId, reason),
    ({ appointmentId, receivingProviderId }) => ({ kind: 'patch', id: appointmentId, patch: { providerId: receivingProviderId } }),
  );
}

export function useReorderQueueItemCommand() {
  return useQueueMutation(
    ({ appointmentId, queueOrder }: { appointmentId: string; queueOrder: number }) =>
      schedulingApi.reorder(appointmentId, queueOrder),
    ({ appointmentId, queueOrder }) => ({ kind: 'patch', id: appointmentId, patch: { queueOrder } }),
  );
}

export function useCorrectAppointmentStatusCommand() {
  return useQueueMutation(
    ({ appointmentId, targetStatus, reason }: {
      appointmentId: string;
      targetStatus: CorrectableStatus;
      reason?: string;
    }) => schedulingApi.correctStatus(appointmentId, targetStatus, reason),
    ({ appointmentId, targetStatus }) => ({ kind: 'patch', id: appointmentId, patch: { status: targetStatus } }),
  );
}

export function useSetQueueOperationalPriorityCommand() {
  return useQueueMutation(
    ({ appointmentId, priority }: {
      appointmentId: string;
      priority: 'Routine' | 'Priority' | 'Emergency';
    }) => schedulingApi.setOperationalPriority(appointmentId, priority),
    ({ appointmentId, priority }) => ({ kind: 'patch', id: appointmentId, patch: { operationalPriority: priority } }),
  );
}

export function useRecordQueueVitalsCommand(visitId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: QueueVitalsBody) => schedulingApi.recordVisitVitals(visitId, body),
    onSuccess: (visit) => {
      queryClient.setQueryData(schedulingKeys.visit(visit.id), visit);
      void queryClient.invalidateQueries({ queryKey: schedulingKeys.queue() });
    },
  });
}

/** Quick-register a patient inline during booking (name + phone). Invalidates patient search cache. */
export function useQuickRegisterPatientCommand() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: QuickRegisterPatientRequest) => schedulingApi.quickRegisterPatient(body),
    onSuccess: () => {
      // Invalidate all patient search caches so newly registered patients appear in subsequent searches.
      void queryClient.invalidateQueries({ queryKey: ['scheduling', 'patient-search'] });
    },
  });
}

export function useSwapQueueItemsCommand() {
  return useQueueMutation(
    (items: {
      appointmentId: string;
      otherAppointmentId: string;
      expectedQueueOrder: number;
      expectedOtherQueueOrder: number;
    }) => schedulingApi.swapQueueItems(
      items.appointmentId,
      items.otherAppointmentId,
      items.expectedQueueOrder,
      items.expectedOtherQueueOrder,
    ),
    items => ({
      kind: 'swap',
      firstId: items.appointmentId,
      secondId: items.otherAppointmentId,
      firstOrder: items.expectedQueueOrder,
      secondOrder: items.expectedOtherQueueOrder,
    }),
  );
}

export function useQuickPatientDuplicateCheckCommand() {
  return useMutation({ mutationFn: (body: { fullName: string; phone: string }) => schedulingApi.checkPatientDuplicates(body) });
}
